import { Attributes, TypeAttribute, TypeAttributeSource } from '@trace/types';
import { useContext, useEffect, useState } from 'react';
import { useQuery, UseQueryResult } from 'react-query';

import { ApiContext } from '../Api/Api';
import { URLStateContext } from '../URLState/URLState';
import { ATTRIBUTE_NAME_MAP } from '../../constants/Attributes';
import getSelectableAttributes, { AttributesInput } from '../../utils/getSelectableAttributes';
import getTimeRange from '../../utils/time/getTimeRange';

type HookPayload = {
  attributes: TypeAttribute[];
  attributesValues: Attributes;
  isEmpty: boolean;
  isFetched: boolean;
};

export type HookResult = Pick<UseQueryResult, 'isError' | 'isFetching' | 'refetch'> & HookPayload;

const useAttributes = (source: TypeAttributeSource): HookResult => {
  const { get } = useContext(ApiContext);
  const { dateFrom, dateTo, demo, duration } = useContext(URLStateContext);
  const { dateFrom: start, dateTo: end } = getTimeRange(duration, dateFrom, dateTo);
  const [payload, setPayload] = useState<Omit<HookPayload, 'isFetched'>>({
    attributes: [],
    attributesValues: {},
    isEmpty: false,
  });

  const {
    data: dataAttributes,
    isError: isErrorAttributes,
    isFetching: isFetchingAttributes,
    isSuccess: isSuccessAttributes,
    refetch,
  } = useQuery<TypeAttribute[]>(['attributes', demo, source], async () =>
    get(`/attributes?source=${source}`),
  );
  const {
    data: dataAttributesValues,
    isError: isErrorAttributesValues,
    isFetching: isFetchingAttributesValues,
    isSuccess: isSuccessAttributesValues,
  } = useQuery<AttributesInput>(
    ['attributesValues', { dataAttributes, start, end }],
    async () =>
      get(
        `/attributes/${(dataAttributes as TypeAttribute[])
          .filter((attribute) => ATTRIBUTE_NAME_MAP[attribute])
          .join(',')}?start=${start}&end=${end}`,
      ),
    {
      enabled: Array.isArray(dataAttributes),
    },
  );

  /**
   * Use a side-effect to:
   *  - Update the `attributesValues` state, whenever the attributesValues query resolves
   */
  useEffect(() => {
    if (dataAttributes && dataAttributesValues) {
      const attributesValues = getSelectableAttributes(dataAttributesValues);
      const attributes = Object.entries(attributesValues)
        .filter(([, values]) => values && values.length)
        .map(([key]) => key) as TypeAttribute[];

      setPayload({
        isEmpty: attributes.length === 0,
        attributes,
        attributesValues,
      });
    }
  }, [dataAttributes, dataAttributesValues]);

  return {
    isError: isErrorAttributes || isErrorAttributesValues,
    isFetched: isSuccessAttributes && isSuccessAttributesValues,
    isFetching: isFetchingAttributes || isFetchingAttributesValues,
    refetch,
    ...payload,
  };
};

export default useAttributes;
